import { Component, OnDestroy, OnInit } from '@angular/core';
import { dayjs } from '@karve.it/core';
import { gql } from 'graphql-tag';
import { EMPTY, Subject, Subscription, forkJoin } from 'rxjs';
import { catchError, debounceTime, switchMap, take, tap } from 'rxjs/operators';

import {
  DataOnlyDocument,
} from '../../../generated/graphql.generated';
import { PlusApollo } from '../../core/auth/graphql.module';
import { GraphQLModule } from '../../core/public-api';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { parseEmbeddedUrl, parseHyperlink } from '../../utilities/generic.util';
import { DashboardStore } from '../dashboardv2.store';

const CALC_OUTPUT_PREFIX = 'calculatorOutput__';

const UPDATE_JOB_METADATA = gql`
  mutation updateJobMetadata($updateJobs: [UpdateJobInput]!) {
    updateJobs(updateJobs: $updateJobs) {
      isSuccess
    }
  }
`;

// Requirement: `#6982` - FP notification status per job. The label is what gets stored, so reports
// that read `calculator_otrFpNotificationStatus` show the same words the dashboard does.
const FP_NOTIFICATION_STATUSES = [
  { label: 'FP Email Sent', color: 'green' },
  { label: 'Pending FP approval', color: 'orange' },
  { label: 'Not sent', color: 'red' },
  { label: 'FP Accepted', color: 'green' },
  { label: 'Ready for Send', color: 'yellow' },
] as const;

// Requirement: `#6982` - the franchise executing the move, as abbreviated on the OTR Job Tracker
// sheet. A label only: choosing one does not share the job or change anything else.
const EXECUTING_FRANCHISES = [
  'KC', 'IND', 'STL', 'CINCY', 'PHX', 'SLC', 'CHAR', 'DEN', 'TWIN', 'LEH', 'OKC',
  'NWA', 'TUL', 'VAN', 'VAN ISL', 'KW/GTA', 'OTR', 'PORT', 'EDMT', 'HON', 'RENO',
];

const EMPTY_OR_MISSING = new Set([
  'none', '', 'notes',
  'calculator_otp_scopesconfirmed', 'calculator_otp_rentalreserved',
  'calculator_otp_hotelsbooked', 'calculator_otp_hotelsdocuploaded',
  'calculator_otp_highvaluedeclared', 'calculator_otp_tendaycall',
  'calculator_otp_sevendaycall', 'calculator_otp_notes',
  'calculator_otp_dldriveonfile', 'calculator_otp_dotmedicalonfile',
  'calculator_otp_contractscompleted', 'calculator_otp_jobshared',
]);

interface OtrEvent {
  jobCode: string;
  jobId: string;
  customer: string;
  franchise: string;
  salesAgent: string;
  eventStatus: string;
  eventType: string;
  eventBookingDate: string;
  jobPaidTotal: string;
  jobTotal: string;
  discountedSubTotal: string;
  // Days from today to the event: 0 on the day, negative once it has passed.
  daysUntilMove: number;
  bolOriginSigned: boolean;
  customerPacketSigned: boolean;
  eomChecklistCreated: boolean;
  // Manual fields (persisted via job metadata, shared across all events on the same job)
  fpNotificationStatus: string | null;
  executingFranchise: string | null;
  visualInventorySigned: boolean;
  scopeConfirmed: boolean;
  inventoryChecklist: boolean;
  itineraryCreated: boolean;
  rentalReserved: boolean;
  highValueDeclared: boolean;
  tenDayCall: boolean;
  sevenDayCall: boolean;
  jobShared: boolean;
  driverDlOnFile: boolean;
  dotMedicalOnFile: boolean;
  contractsCompleted: boolean;
  notes: string;
  eventId: string;
}

interface DayGroup {
  date: string;
  displayDate: string;
  events: OtrEvent[];
  eventCount: number;
  daysUntilMove: number | null;
}

const OTR_DATA_COLUMNS = [
  'customer',
  'eventStatus',
  'eventType',
  'eventBookingDate',
  'jobPaidTotal',
  'jobTotal',
  'discountedSubTotal',
  'salesAgent',
  'bolOriginSigned',
  'customerPacketSigned',
  'eomChecklistCreated',
  'event',
  'job',
];

// Requirement: `#6982` - one row per job, on its first scheduled day: OTR plans the whole move at the
// 10-day mark, so the later days of the same move are noise. The backend judges "first" against all
// of the job's events, not only those in the selected period.
const FIRST_EVENT_EXCLUDED_TYPES = ['virtualEstimate', 'estimating', 'otr-fees'];

const OTR_JOB_COLUMNS = [
  'job',
  'calculator_visualInventorySigned',
  'calculator_scopesConfirmed',
  'calculator_rentalReserved',
  'calculator_highValueDeclared',
  'calculator_tenDayCall',
  'calculator_sevenDayCall',
  'calculator_jobShared',
  'calculator_driverDlOnFile',
  'calculator_dotMedicalOnFile',
  'calculator_contractsCompleted',
  'calculator_notes',
  'calculator_otrFpNotificationStatus',
  'calculator_otrExecutingFranchise',
  'calculator_inventoryChecklist',
  'calculator_itineraryCreated',
];

// Each field is due either at booking or N days before the event. A due field that is not complete
// is "overdue": its cell is tinted and the row turns red. Fields not listed here never turn a row red
// (EOM checklist and high-value declaration happen during or after the move; FP notification status,
// executing franchise, inventory checklist and itinerary created are informational until ops decides).
interface ReadinessField {
  key: keyof OtrEvent;
  due: 'booking' | number;
}

const READINESS_FIELDS: ReadinessField[] = [
  // Due from the moment the job is booked
  { key: 'customerPacketSigned', due: 'booking' },
  { key: 'visualInventorySigned', due: 'booking' },
  { key: 'jobPaidTotal', due: 'booking' },
  { key: 'jobShared', due: 'booking' },
  // 10 days out
  { key: 'scopeConfirmed', due: 10 },
  { key: 'bolOriginSigned', due: 10 },
  { key: 'rentalReserved', due: 10 },
  { key: 'tenDayCall', due: 10 },
  { key: 'driverDlOnFile', due: 10 },
  { key: 'dotMedicalOnFile', due: 10 },
  { key: 'contractsCompleted', due: 10 },
  // 7 days out
  { key: 'sevenDayCall', due: 7 },
];

@Component({
  selector: 'app-dashboard-otr-ops-readiness',
  standalone: false,
  templateUrl: './dashboard-otr-ops-readiness.component.html',
  styleUrls: [
    '../dashboardv2.component.scss',
    './dashboard-otr-ops-readiness.component.scss',
  ],
})
export class DashboardOtrOpsReadinessComponent implements OnInit, OnDestroy {

  // Each dropdown leads with a blank option (value null) so a choice can be undone.
  readonly fpNotificationOptions = [
    { label: '', value: null, color: null },
    ...FP_NOTIFICATION_STATUSES.map(s => ({ label: s.label, value: s.label, color: s.color })),
  ];
  readonly executingFranchiseOptions = [
    { label: '', value: null },
    ...EXECUTING_FRANCHISES.map(f => ({ label: f, value: f })),
  ];

  dayGroups: DayGroup[] = [];
  loading = true;
  lastReload = 0;
  totalEvents = 0;
  expandedDays = new Set<string>();

  private reload$ = new Subject<void>();
  private subs: Subscription[] = [];

  constructor(
    private dashboardStore: DashboardStore,
    private graphqlModule: GraphQLModule,
    private apollo: PlusApollo,
    private notify: FreyaNotificationsService,
    private detailsHelper: DetailsHelperService,
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.dashboardStore.unixPeriod$.subscribe(() => {
        this.reload$.next();
      }),
    );

    this.subs.push(
      this.reload$.pipe(
        debounceTime(100),
        switchMap(() => {
          const zone = this.graphqlModule.zone.value;
          if (!zone) {
            this.loading = false;
            return EMPTY;
          }

          this.loading = true;
          this.totalEvents = 0;
          this.flushNotes();

          return this.dashboardStore.unixPeriod$.pipe(
            take(1),
            switchMap(({ start, end }) => {
              return forkJoin({
                events: this.apollo.query({
                  query: DataOnlyDocument,
                  fetchPolicy: 'no-cache',
                  variables: {
                    start,
                    end,
                    periodType: 'eventStart',
                    groupBy: 'event',
                    dataColumns: OTR_DATA_COLUMNS,
                    filter: {
                      job: {
                        getJobsOfState: ['open', 'closed', 'archived'],
                      },
                      event: {
                        getEventsOfStatus: ['booked', 'confirmed', 'completed'],
                        getEventsOfSharedStatus: ['not-shared'],
                      },
                      firstEventOfJob: {
                        excludeTypes: FIRST_EVENT_EXCLUDED_TYPES,
                      },
                    },
                    sort: 'eventBookingDate ASC',
                    performance: 'skip-count',
                    skip: 0,
                    limit: -1,
                    zone,
                  } as any,
                }),
                jobs: this.apollo.query({
                  query: DataOnlyDocument,
                  fetchPolicy: 'no-cache',
                  variables: {
                    start,
                    end,
                    periodType: 'eventStart',
                    groupBy: 'job',
                    dataColumns: OTR_JOB_COLUMNS,
                    filter: {
                      job: {
                        getJobsOfState: ['open', 'closed', 'archived'],
                      },
                    },
                    sort: null,
                    performance: 'skip-count',
                    skip: 0,
                    limit: -1,
                    zone,
                  } as any,
                }),
              });
            }),
            tap(({ events, jobs }: { events: any; jobs: any }) => {
              const eventRows: string[][] = events?.data?.previewReport?.data?.rows ?? [];
              const eventHeaders: string[] = events?.data?.previewReport?.data?.headers ?? [];
              const jobRows: string[][] = jobs?.data?.previewReport?.data?.rows ?? [];
              const jobHeaders: string[] = jobs?.data?.previewReport?.data?.headers ?? [];

              const jobData = this.buildJobMap(jobHeaders, jobRows);
              this.dayGroups = this.groupByDay(eventHeaders, eventRows, jobData);
              this.totalEvents = this.dayGroups.reduce((sum, g) => sum + g.eventCount, 0);
              this.loading = false;
              this.lastReload = Math.floor(Date.now() / 1000);
            }),
            catchError(() => {
              this.loading = false;
              this.lastReload = Math.floor(Date.now() / 1000);
              this.notify.error('Error', 'Failed to load OTR ops readiness data');
              return EMPTY;
            }),
          );
        }),
      ).subscribe(),
    );

    this.reload$.next();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.flushNotes();
    this.reload$.complete();
  }

  private flushNotes(): void {
    this.notesDebounce.forEach(({ subject, sub }) => {
      subject.complete();
      sub.unsubscribe();
    });
    this.notesDebounce.clear();
  }

  openJobDetails(event: Event, evt: OtrEvent): void {
    event.preventDefault();
    if (evt.jobId) {
      this.detailsHelper.open('job', { id: evt.jobId });
    }
  }

  openJobNewTab(evt: OtrEvent): void {
    if (evt.jobId) {
      window.open('/jobs/' + evt.jobId + '/overview', '_blank');
    }
  }

  refresh(): void {
    this.reload$.next();
  }

  toggleDayExpanded(date: string): void {
    if (this.expandedDays.has(date)) {
      this.expandedDays.delete(date);
    } else {
      this.expandedDays.add(date);
    }
  }

  isDayExpanded(date: string): boolean {
    return this.expandedDays.has(date);
  }

  // Returns true if a field is "complete" — for boolean fields, just the
  // value itself. For jobPaidTotal (string), complete means non-zero.
  private isFieldComplete(evt: OtrEvent, key: keyof OtrEvent): boolean {
    const val = evt[key];
    if (typeof val === 'boolean') return val;
    if (key === 'jobPaidTotal') {
      return !!val && val !== '0' && val !== '$0.00' && val !== '\u2014';
    }
    return !!val;
  }

  private isFieldDue(field: ReadinessField, daysUntilMove: number): boolean {
    return field.due === 'booking' || daysUntilMove <= field.due;
  }

  // Returns true if the field is due for this event and not complete (its cell is tinted).
  isOverdue(evt: OtrEvent, key: keyof OtrEvent): boolean {
    const field = READINESS_FIELDS.find(f => f.key === key);
    return !!field && this.isFieldDue(field, evt.daysUntilMove) && !this.isFieldComplete(evt, key);
  }

  // Returns true if any due item is incomplete (row should be red).
  hasOverdueItems(evt: OtrEvent): boolean {
    return READINESS_FIELDS.some(f => this.isOverdue(evt, f.key));
  }

  // Returns true if all events in a day group are fully ready.
  isDayReady(day: DayGroup): boolean {
    return day.events.every(e => !this.hasOverdueItems(e));
  }

  setJobField(evt: OtrEvent, field: 'fpNotificationStatus' | 'executingFranchise', value: string | null, metadataKey: string): void {
    this.syncJobEvents(evt, field, value);
    this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + metadataKey]: value ?? '' });
  }

  toggleCheckbox(evt: OtrEvent, field: keyof OtrEvent, checked: boolean, metadataKey: string = field as string): void {
    this.syncJobEvents(evt, field, checked);
    this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + metadataKey]: String(checked) });
  }

  // Metadata is per-job, not per-event, so a change on one event is mirrored on every other event
  // of the same job (including events on different days).
  private syncJobEvents(evt: OtrEvent, field: keyof OtrEvent, value: unknown): void {
    (evt as any)[field] = value;
    for (const group of this.dayGroups) {
      for (const e of group.events) {
        if (e.jobId === evt.jobId && e !== evt) {
          (e as any)[field] = value;
        }
      }
    }
  }

  onNotesInput(evt: OtrEvent): void {
    if (!this.notesDebounce.has(evt.jobId)) {
      const subject = new Subject<string>();
      const sub = subject.pipe(debounceTime(800)).subscribe((val) => {
        this.saveJobMetadata(evt.jobId, { [CALC_OUTPUT_PREFIX + 'notes']: val });
      });
      this.notesDebounce.set(evt.jobId, { subject, sub });
    }
    this.notesDebounce.get(evt.jobId)!.subject.next(evt.notes);
  }

  private savingJobs = new Set<string>();
  private pendingMetadata = new Map<string, Record<string, string>>();
  private notesDebounce = new Map<string, { subject: Subject<string>; sub: Subscription }>();

  private saveJobMetadata(jobId: string, metadata: Record<string, string>): void {
    if (this.savingJobs.has(jobId)) {
      const existing = this.pendingMetadata.get(jobId) ?? {};
      Object.assign(existing, metadata);
      this.pendingMetadata.set(jobId, existing);
      return;
    }
    this.savingJobs.add(jobId);
    this.apollo.mutate({
      mutation: UPDATE_JOB_METADATA,
      variables: {
        updateJobs: { jobId, metadata },
      },
    }).subscribe({
      next: (res: any) => {
        const results = res?.data?.updateJobs;
        const ok = Array.isArray(results)
          ? results.every((r: any) => r?.isSuccess)
          : !!results?.isSuccess;
        if (!ok) {
          this.savingJobs.delete(jobId);
          this.pendingMetadata.delete(jobId);
          this.notify.error('Error', 'Failed to save OTR data');
          return;
        }
        this.savingJobs.delete(jobId);
        const pending = this.pendingMetadata.get(jobId);
        if (pending) {
          this.pendingMetadata.delete(jobId);
          this.saveJobMetadata(jobId, pending);
        }
      },
      error: () => {
        this.savingJobs.delete(jobId);
        this.pendingMetadata.delete(jobId);
        this.notify.error('Error', 'Failed to save OTR data');
      },
    });
  }

  private parseName(raw: string): string {
    const embedded = parseEmbeddedUrl(raw);
    if (embedded) {
      return embedded.before;
    }
    const hyperlink = parseHyperlink(raw);
    if (hyperlink) {
      return hyperlink.text;
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return [parsed.givenName, parsed.familyName].filter(Boolean).join(' ') || raw;
      }
    } catch {}
    return raw;
  }

  private boolVal(raw: string): boolean {
    return raw === 'true' || raw === '1' || raw === 'Yes';
  }

  // A stored option, matched case-insensitively and returned in its canonical spelling; anything
  // else (unset, blank, a value no longer offered) reads as no selection.
  private optionVal(raw: string, options: readonly string[]): string | null {
    const needle = (raw ?? '').trim().toLowerCase();
    return options.find(o => o.toLowerCase() === needle) ?? null;
  }

  private buildJobMap(headers: string[], rows: string[][]): Map<string, Record<string, any>> {
    const getVal = (row: string[], col: string) => {
      const idx = headers.indexOf(col);
      if (idx === -1) {
        console.warn(`[otr-ops-readiness] missing report column: ${col}`);
        return '';
      }
      return row[idx] ?? '';
    };
    const map = new Map<string, Record<string, any>>();

    for (const row of rows) {
      const jobRaw = getVal(row, 'Job');
      const jobEmbedded = parseEmbeddedUrl(jobRaw);
      let jobId = '';
      if (jobEmbedded) {
        const segments = jobEmbedded.url.split('/').filter(Boolean);
        jobId = segments[segments.length - 1] || '';
      }
      if (!jobId) continue;

      const fields: Record<string, any> = {};
      const fieldMap: Record<string, string> = {
        visualInventorySigned: 'Visual Inventory Signed',
        scopesConfirmed: 'Scopes Confirmed',
        rentalReserved: 'Rental Reserved',
        highValueDeclared: 'High Value Declared',
        tenDayCall: 'Ten Day Call',
        sevenDayCall: 'Seven Day Call',
        jobShared: 'Job Shared',
        driverDlOnFile: 'Driver Dl On File',
        dotMedicalOnFile: 'Dot Medical On File',
        contractsCompleted: 'Contracts Completed',
        inventoryChecklist: 'Inventory Checklist',
        itineraryCreated: 'Itinerary Created',
      };

      for (const [key, header] of Object.entries(fieldMap)) {
        fields[key] = this.boolVal(getVal(row, header));
      }

      let notes = getVal(row, 'Notes');
      if (!notes || EMPTY_OR_MISSING.has(notes.toLowerCase())) {
        notes = '';
      }
      fields.notes = notes;
      fields.fpNotificationStatus = this.optionVal(
        getVal(row, 'Otr Fp Notification Status'),
        FP_NOTIFICATION_STATUSES.map(s => s.label),
      );
      fields.executingFranchise = this.optionVal(getVal(row, 'Otr Executing Franchise'), EXECUTING_FRANCHISES);

      map.set(jobId, fields);
    }

    return map;
  }

  private groupByDay(headers: string[], rows: string[][], jobData: Map<string, Record<string, any>>): DayGroup[] {
    const getVal = (row: string[], col: string) => {
      const idx = headers.indexOf(col);
      if (idx === -1) {
        console.warn(`[otr-ops-readiness] missing report column: ${col}`);
        return '';
      }
      return row[idx] ?? '';
    };
    const map = new Map<string, OtrEvent[]>();
    // The backend's `firstEventOfJob` filter already returns one event per job (its first scheduled
    // one, estimates excluded), so every row here is shown.

    for (const row of rows) {
      const eventBookingDate = getVal(row, 'Event Booking Date');
      if (!eventBookingDate || eventBookingDate === 'Not booked') continue;

      const eventType = getVal(row, 'Event Type');

      const jobRaw = getVal(row, 'Job');
      const jobEmbedded = parseEmbeddedUrl(jobRaw);
      let jobCode = jobRaw;
      let jobId = '';
      if (jobEmbedded) {
        jobCode = jobEmbedded.before;
        const segments = jobEmbedded.url.split('/').filter(Boolean);
        jobId = segments[segments.length - 1] || '';
      }

      const date = dayjs(eventBookingDate).format('YYYY-MM-DD');
      const jobFields = jobData.get(jobId) ?? {};

      const daysUntilMove = dayjs(date).diff(dayjs().startOf('day'), 'day');

      const event: OtrEvent = {
        jobCode,
        jobId,
        customer: this.parseName(getVal(row, 'Customer')),
        franchise: '',
        salesAgent: this.parseName(getVal(row, 'Sales Agent')),
        eventStatus: getVal(row, 'Event Status'),
        eventType,
        eventBookingDate,
        jobPaidTotal: getVal(row, 'Job Paid Total'),
        jobTotal: getVal(row, 'Job Total'),
        discountedSubTotal: getVal(row, 'Net subtotal'),
        daysUntilMove,
        bolOriginSigned: getVal(row, 'BOL Origin Signed') === 'Yes',
        customerPacketSigned: getVal(row, 'Customer Packet Signed') === 'Yes',
        eomChecklistCreated: getVal(row, 'EOM Checklist Created') === 'Yes',
        fpNotificationStatus: jobFields.fpNotificationStatus ?? null,
        executingFranchise: jobFields.executingFranchise ?? null,
        visualInventorySigned: jobFields.visualInventorySigned ?? false,
        scopeConfirmed: jobFields.scopesConfirmed ?? false,
        inventoryChecklist: jobFields.inventoryChecklist ?? false,
        itineraryCreated: jobFields.itineraryCreated ?? false,
        rentalReserved: jobFields.rentalReserved ?? false,
        highValueDeclared: jobFields.highValueDeclared ?? false,
        tenDayCall: jobFields.tenDayCall ?? false,
        sevenDayCall: jobFields.sevenDayCall ?? false,
        jobShared: jobFields.jobShared ?? false,
        driverDlOnFile: jobFields.driverDlOnFile ?? false,
        dotMedicalOnFile: jobFields.dotMedicalOnFile ?? false,
        contractsCompleted: jobFields.contractsCompleted ?? false,
        notes: jobFields.notes ?? '',
        eventId: getVal(row, 'Event'),
      };

      if (!map.has(date)) {
        map.set(date, []);
      }
      map.get(date)!.push(event);
    }

    const groups: DayGroup[] = [];
    const sortedDates = Array.from(map.keys()).sort();

    for (const d of sortedDates) {
      const events = map.get(d)!.slice().sort((a, b) => {
        if (!a.eventBookingDate || a.eventBookingDate === 'Not booked') return 1;
        if (!b.eventBookingDate || b.eventBookingDate === 'Not booked') return -1;
        return a.eventBookingDate.localeCompare(b.eventBookingDate);
      });

      const dateObj = dayjs(d);
      const today = dayjs().startOf('day');
      const daysUntilMove = dateObj.isAfter(today) ? dateObj.diff(today, 'day') : null;

      groups.push({
        date: d,
        displayDate: dayjs(d).format('dddd, MMMM D, YYYY'),
        events,
        eventCount: events.length,
        daysUntilMove,
      });
    }

    return groups;
  }
}